import React, { useContext, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";
import { useUniqueId } from "../hooks/useUniqueId";
import { DropdownInput } from "../DropdownInput";
import {
  DropdownList,
  DropdownListItem,
  DropdownListStaticContent,
} from "../DropdownList";
import { Checkbox } from "../Checkbox";
import { CopyText } from "../CopyText";

import styles from "./DropdownSelect.scss";

const MenuItemCheckbox: DropdownListItem = (props) => {
  const {
    item,
    label,
    selected,
    disabled,
    selectHandler,
    ...restProps
  } = props;
  return (
    <Checkbox
      onlyDisplay
      checkboxProps={restProps}
      wrapperProps={{ customClass: styles.listItemCheckbox }}
      value={item}
      checked={selected}
      disabled={disabled}
      onChange={selectHandler}
    >
      {label || item}
    </Checkbox>
  );
};

MenuItemCheckbox.propTypes = {
  item: PropTypes.string.isRequired,
  label: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  selectHandler: PropTypes.func.isRequired,
};

MenuItemCheckbox.defaultProps = {
  label: undefined,
};

const MenuItemText: DropdownListItem = (props) => {
  const {
    item,
    label,
    selected,
    disabled,
    selectHandler,
    ...restProps
  } = props;
  return (
    <CopyText
      customClass={cx(styles.listItemText, { "is-selected": selected })}
      {...restProps}
    >
      {label || item}
    </CopyText>
  );
};

MenuItemText.propTypes = {
  item: PropTypes.string.isRequired,
  label: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  selectHandler: PropTypes.func.isRequired,
};

MenuItemText.defaultProps = {
  label: undefined,
};

export const DropdownSelectDefaultTagName = "div";
export type DropdownSelectDefaultTag = typeof DropdownSelectDefaultTagName;

export const dropdownSelectNativeVariants = [
  "none",
  "byHover",
  "byViewport",
  "byHoverAndViewport",
] as const;

const nativeClassMap = {
  none: "nativeNone",
  byHover: "nativeByHover",
  byViewport: "nativeByViewport",
  byHoverAndViewport: "nativeByHoverAndViewport",
} as const;

export type DropdownSelectNativeVariant = typeof dropdownSelectNativeVariants[number];

export type DropdownSelectBaseProps<
  Tag extends React.ElementType = DropdownSelectDefaultTag
> = {
  tag?: Tag;
  multiple?: boolean;
  nativeVariant?: DropdownSelectNativeVariant;
  customClass?: string;
  contentCustomClass?: string;
  listCustomClass?: string;
  listItemCustomClass?: string;
  id?: string;
  items: string[];
  itemLabels?: (string | null | undefined)[];
  value?: string[];
  prefix?: string;
  emptyLabel?: string;
  isValid?: boolean;
  onChange?: (selectedItem: string[]) => void;
  afterOpen?: (selectedItems: string[] | undefined) => void;
  afterClose?: (selectedItems: string[] | undefined) => void;
  isOpen?: boolean;
  minSelectionCount?: number;
  maxSelectionCount?: number;
  valueRenderer?: (prefix: string, value: string[] | undefined) => string;
};

const defaultProps = {
  tag: DropdownSelectDefaultTagName,
  children: "",
  itemLabels: undefined,
  multiple: false,
  emptyLabel: "",
  nativeVariant: "byHover",
  customClass: undefined,
  contentCustomClass: undefined,
  listCustomClass: undefined,
  listItemCustomClass: undefined,
  id: undefined,
  value: [] as string[],
  prefix: "",
  isValid: undefined,
  onChange: undefined,
  afterOpen: undefined,
  afterClose: undefined,
  isOpen: undefined,
  minSelectionCount: 0,
  maxSelectionCount: undefined,
  valueRenderer: (prefix: string, value: string[] | undefined) =>
    value && value.length > 0 ? `${prefix} (${value.length})` : "",
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  DropdownSelectBaseProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  tag: PropTypes.elementType,
  nativeVariant: PropTypes.oneOf(dropdownSelectNativeVariants),
  multiple: PropTypes.bool,
  emptyLabel: PropTypes.string,
  customClass: PropTypes.string,
  contentCustomClass: PropTypes.string,
  listCustomClass: PropTypes.string,
  listItemCustomClass: PropTypes.string,
  id: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  itemLabels: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.arrayOf(PropTypes.string.isRequired),
  prefix: PropTypes.string,
  isValid: PropTypes.bool,
  onChange: PropTypes.func,
  afterOpen: PropTypes.func,
  afterClose: PropTypes.func,
  isOpen: PropTypes.bool,
  minSelectionCount: PropTypes.number,
  maxSelectionCount: PropTypes.number,
  valueRenderer: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
} as const;

export type DropdownSelectProps<
  Tag extends React.ElementType = DropdownSelectDefaultTag
> = DropdownSelectBaseProps<Tag> &
  Omit<React.ComponentPropsWithRef<Tag>, keyof DropdownSelectBaseProps<Tag>>;

export type DropdownSelectPropsWithoutRef<
  Tag extends React.ElementType = DropdownSelectDefaultTag
> = DropdownSelectBaseProps<Tag> &
  Omit<React.ComponentPropsWithoutRef<Tag>, keyof DropdownSelectBaseProps<Tag>>;

const SelectContext = React.createContext<
  Required<
    Pick<
      DropdownSelectPropsWithoutRef,
      "multiple" | "emptyLabel" | "nativeVariant" | "items"
    >
  > &
    Partial<Pick<DropdownSelectPropsWithoutRef, "itemLabels">> & {
      selectRef: React.MutableRefObject<HTMLSelectElement | null> | undefined;
    }
>({
  multiple: defaultProps.multiple,
  emptyLabel: defaultProps.emptyLabel,
  nativeVariant: defaultProps.nativeVariant,
  items: [],
  itemLabels: undefined,
  selectRef: undefined,
});

const NativeSelect: DropdownListStaticContent = ({
  value,
  validate,
  setValue,
  setIsValid,
}) => {
  const { isOpen } = useContext(DropdownInput.Context);
  const { items, itemLabels, multiple, emptyLabel, selectRef } = useContext(
    SelectContext
  );
  const selectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = Array.from(event.currentTarget.options)
      .filter((option) => option.selected)
      .map((option) => option.value);
    setValue(newValue);
    setIsValid(validate(newValue));
  };

  const itemValueLabelPairs: {
    value: string;
    label: string | undefined | null;
  }[] = [];
  for (let i = 0; i < items.length; i += 1) {
    itemValueLabelPairs.push({
      value: items[i],
      label: itemLabels && itemLabels[i],
    });
  }

  return (
    <div
      className={cx(styles.selectWrapper, {
        [styles.selectWrapperMultiple]: multiple,
        [styles.selectWrapperOpen]: isOpen,
      })}
    >
      <select
        ref={selectRef}
        multiple={multiple}
        className={cx(styles.select, styles.input)}
        value={multiple ? value : value[0]}
        onChange={selectChange}
      >
        {!multiple && emptyLabel && <option aria-label={emptyLabel} value="" />}
        {
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          !multiple && emptyLabel === "" && <option hidden disabled value="" />
        }
        {itemValueLabelPairs.map((item) => {
          return (
            <option
              key={item.value}
              className={styles.option}
              value={item.value}
            >
              {item.label || item.value}
            </option>
          );
        })}
      </select>
    </div>
  );
};

NativeSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  validate: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  setIsValid: PropTypes.func.isRequired,
};

const singleUpdateValue = (value: string[], items: string[], item: string) => {
  return [item];
};

const DropdownSelectWithRef = <
  Tag extends React.ElementType = DropdownSelectDefaultTag
>(
  props: DropdownSelectPropsWithoutRef<Tag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
): JSX.Element => {
  const {
    multiple = defaultProps.multiple,
    emptyLabel = defaultProps.emptyLabel,
    nativeVariant = defaultProps.nativeVariant,
    id = defaultProps.id,
    value = defaultProps.value,
    isValid = defaultProps.isValid,
    isOpen = defaultProps.isOpen,
    customClass = defaultProps.customClass,
    contentCustomClass = defaultProps.contentCustomClass,
    listCustomClass = defaultProps.listCustomClass,
    items,
    itemLabels = defaultProps.itemLabels,
    children,
    ...restProps
  } = props;

  const listRef = useRef(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  const setElementRef = (
    elementRef: React.MutableRefObject<HTMLElement | null>,
    forwardedRef:
      | React.MutableRefObject<HTMLElement | null>
      | React.RefCallback<HTMLElement>
  ) => (element: HTMLElement | null) => {
    // eslint-disable-next-line no-param-reassign
    elementRef.current = element;
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
    }
  };

  const dropdownSelectId =
    id || `dropdown-multi-select-${useUniqueId("DropdownSelect", props) + 1}`;

  return (
    <SelectContext.Provider
      value={{
        multiple,
        emptyLabel,
        nativeVariant,
        items,
        itemLabels,
        selectRef,
      }}
    >
      <DropdownList
        ref={setElementRef(listRef, ref)}
        value={value}
        id={dropdownSelectId}
        isValid={isValid}
        itemComponent={multiple ? MenuItemCheckbox : MenuItemText}
        staticContent={NativeSelect}
        customClass={cx(
          styles.wrapper,
          customClass,
          styles[nativeClassMap[nativeVariant]]
        )}
        contentCustomClass={cx(styles.content, contentCustomClass)}
        listCustomClass={cx(styles.list, listCustomClass)}
        isOpen={isOpen}
        items={items}
        itemLabels={itemLabels}
        updateValue={multiple ? undefined : singleUpdateValue}
        closeOnUpdate={!multiple}
        {...restProps}
      >
        {children}
      </DropdownList>
    </SelectContext.Provider>
  );
};

export const DropdownSelect = (React.forwardRef(
  DropdownSelectWithRef
) as unknown) as (<Tag extends React.ElementType = DropdownSelectDefaultTag>(
  props: DropdownSelectProps<Tag>
) => ReturnType<typeof DropdownSelectWithRef>) &
  ReactComponentStaticProps<DropdownSelectProps, "tag">;

DropdownSelect.displayName = "DropDownSelect";

DropdownSelect.propTypes = propTypes;

DropdownSelect.defaultProps = validatedDefaultProps;

export default DropdownSelect;
